import { Box, Stack, Typography } from "@mui/material";
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridGroupingColDefOverride,
  GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { WorkflowDefinition } from "../../../../api/types/workflowTypes";
import WorkflowTemplateAvatar from "../../../common/WorkflowTemplateAvatar";
import WorkflowDefinitionActions from "./WorkflowDefinitionActions";

const gridRowGroupingModel: GridRowGroupingModel = ["category"];

const gridColumnVisibilityModel: GridColumnVisibilityModel = {
  category: false,
  name: false,
};

const groupingColumnDefinition: GridGroupingColDefOverride<WorkflowDefinition> = {
  leafField: "name",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  flex: 3,
};

const getRowId = (row: WorkflowDefinition) => row.workflowType;

const NameCell = ({ row }: { row: WorkflowDefinition }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flex: 1, minWidth: 0 }}>
    <Box sx={{ width: "2.5rem" }} />
    <WorkflowTemplateAvatar workflowType={row.workflowType} category={row.category} />
    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">{row.name}</Typography>
      <Typography variant="caption" noWrap color="textSecondary">
        {row.description}
      </Typography>
    </Stack>
  </Stack>
);

const getColumnDefinitions = (
  onWorkflowDefinitionUpdate: (updatedDefinition: WorkflowDefinition) => void
): GridColDef<WorkflowDefinition>[] => [
  {
    field: nameof<WorkflowDefinition>("category"),
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        return <Typography variant="subtitle2">{stringifyValue(rowNode.groupingKey)}</Typography>;
      }

      return null;
    },
  },
  {
    field: nameof<WorkflowDefinition>("name"),
    headerName: "Name",
    flex: 3,
    minWidth: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <NameCell row={row} />;
    },
  },
  {
    field: "actions",
    headerName: "Active",
    width: 240,
    align: "right",
    headerAlign: "right",
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return (
        <WorkflowDefinitionActions workflowDefinition={row} onWorkflowDefinitionUpdate={onWorkflowDefinitionUpdate} />
      );
    },
  },
];

interface Props {
  rows: WorkflowDefinition[];
  onRowUpdate: (updatedDefinition: WorkflowDefinition) => void;
  loading?: boolean;
}

const WorkflowDefinitionsGrid = ({ rows, onRowUpdate, loading }: Props) => {
  const apiRef = useGridApiRef();

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const columns = useMemo(() => getColumnDefinitions(onRowUpdate), [onRowUpdate]);

  return (
    <DataGrid<WorkflowDefinition>
      apiRef={apiRef}
      rows={rows}
      getRowId={getRowId}
      columns={columns}
      groupingColDef={groupingColumnDefinition}
      rowGroupingModel={gridRowGroupingModel}
      columnVisibilityModel={gridColumnVisibilityModel}
      loading={loading}
      isGroupExpandedByDefault={isGroupExpanded}
      noRowsText="No workflow definitions"
      multilineRows
      disableColumnPinning
      disableColumnReorder
      disableColumnSorting
    />
  );
};

export default WorkflowDefinitionsGrid;
